// client/src/utils/platformAuthCheck.js

/**
 * Fetches integration status for a tenant and splits the currently selected
 * platforms into "missing" (no token on file at all) and "expired"
 * (a token exists but is past its known expiry). Used as a pre-flight check
 * by every posting surface (MediaPost, SchedulePost, TextPost) so a post
 * attempt against a dead integration fails before hitting the publish API.
 *
 * Returns { missing: [], expired: [] } (both arrays of provider keys).
 * On any fetch/parse failure, returns empty arrays so callers fall back to
 * letting the server reject the request.
 */
export async function checkPlatformAuth(baseUrl, tenantId, selectedPlatforms) {
  const result = { missing: [], expired: [] };
  if (!selectedPlatforms?.length) return result;

  try {
    const res = await fetch(`${baseUrl}/api/integrations/status/${tenantId}`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!res.ok) return result;

    const data = await res.json();
    if (!data?.success) return result;

    const statuses = data.integrations || {};
    for (const p of selectedPlatforms) {
      const status = statuses[p];
      if (!status?.connected) {
        result.missing.push(p);
      } else if (status.expired) {
        result.expired.push(p);
      }
    }
  } catch (err) {
    console.warn("[checkPlatformAuth] Could not verify platform authorization:", err?.message);
  }

  return result;
}
